import merge from 'lodash.merge';
import { FC, ReactNode } from 'react';
import config from './dev.config';
import { ProviderConfiguration } from './provider';

import { Backstage, BackstageConfig, createBackstageStore } from './Backstage';
import BackstageHTTPProvider from './BackstageHTTP';
import BackstageLocalProvider from './BackstageLocal';

export { BackstageContext, useBackstage, useConfig, useFlag, useFlags, useVariable } from './Backstage';

declare global {
  interface Window {
    BACKSTAGE_URL: string;
    BACKSTAGE_ENVIRONMENT: string;
  }
}

const isHttpUrl = (value?: string) => typeof value === 'string' && /^(https?:)\/\//.test(value);
const isTokenPlaceholder = (value?: string) =>
  typeof value === 'string' && (value.startsWith('#{') || value.includes('%VITE_'));
// If this file is imported in a non-browser context (i.e. during unit tests), window will be undefined, so we need to check for that before accessing window properties.
const isLocalRuntime = () =>
  typeof window !== 'undefined' &&
  (['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname) ||
    window.location.hostname.endsWith('.local'));

const getRuntimeBackstageUrl = () => {
  if (isHttpUrl(window.BACKSTAGE_URL)) {
    return window.BACKSTAGE_URL;
  }

  const envUrl = import.meta.env.VITE_API_BACKSTAGE_URL;
  return isHttpUrl(envUrl) ? envUrl : undefined;
};

const getRuntimeBackstageEnvironment = () => {
  if (window.BACKSTAGE_ENVIRONMENT && !isTokenPlaceholder(window.BACKSTAGE_ENVIRONMENT)) {
    return window.BACKSTAGE_ENVIRONMENT;
  }

  const envName = import.meta.env.VITE_API_BACKSTAGE_ENVIRONMENT;
  return envName && !isTokenPlaceholder(envName) ? envName : undefined;
};

export const getRuntimeBackstageSettings = () => ({
  url: getRuntimeBackstageUrl(),
  environment: getRuntimeBackstageEnvironment()
});

const providers: ProviderConfiguration[] = [];

if (isLocalRuntime()) {
  providers.push(BackstageLocalProvider(3, { config }));
}

const backstageSettings = getRuntimeBackstageSettings();
if (backstageSettings.url && backstageSettings.environment) {
  providers.push(
    BackstageHTTPProvider(1, {
      url: `${backstageSettings.url}${backstageSettings.environment}/index.json`
    })
  );
}

const backstageStore = createBackstageStore(providers);
let backstageOverrides: BackstageConfig = {};

backstageStore.getState().process.then(() => {
  if (Object.keys(backstageOverrides).length > 0) {
    backstageStore.getState().updateConfig(backstageOverrides);
  }
});

export const applyBackstageConfigurationOverride = (config: BackstageConfig) => {
  backstageOverrides = merge({}, backstageOverrides, config) as BackstageConfig;
  backstageStore.getState().updateConfig(config);
};

// Wraps the application using the new local component
export const BackstageProvider: FC<{ children: ReactNode }> = ({ children }) => {
  return <Backstage store={backstageStore}>{children}</Backstage>;
};

// Standalone function for non-React contexts
export const getVariable = async (key: string) => {
  await backstageStore.getState().process;
  const value = backstageStore.getState().getVariable(key);
  if (value === undefined) {
    throw new Error(`Variable ${key} not found`);
  }

  return value;
};
